import jsPDF from 'jspdf';

const PDF_LOGO = '/assets/logos/wordmark-cream.png';

function loadImageDataUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = src;
  });
}

// Turn the rich notes HTML into a flat list of typed blocks for the PDF.
function parseNotesHtml(html) {
  if (!html) return [];
  const blocks = [];
  try {
    const div = document.createElement('div');
    div.innerHTML = html;
    const walk = (node, depth = 0) => {
      node.childNodes.forEach((n) => {
        if (n.nodeType === 3) {
          const t = (n.textContent || '').replace(/\s+/g, ' ').trim();
          if (t) blocks.push({ type: 'p', text: t });
          return;
        }
        if (n.nodeType !== 1) return;
        const tag = n.tagName.toLowerCase();
        const text = (n.textContent || '').replace(/\s+/g, ' ').trim();
        if (/^h[1-6]$/.test(tag)) {
          if (text) blocks.push({ type: 'h', text });
        } else if (tag === 'li') {
          if (text) blocks.push({ type: 'li', text, depth });
        } else if (tag === 'ul' || tag === 'ol') {
          n.childNodes.forEach((li) => { if (li.nodeType === 1 && li.tagName.toLowerCase() === 'li') walk(n.ownerDocument ? { childNodes: [li] } : li, depth + 1); });
        } else if (tag === 'p' || tag === 'div') {
          if (text && !n.querySelector('ul,ol,h1,h2,h3,h4')) blocks.push({ type: 'p', text });
          else walk(n, depth);
        } else {
          walk(n, depth);
        }
      });
    };
    walk(div);
  } catch { /* ignore */ }
  return blocks;
}

export async function exportCardToPDF(card, tasks = [], sections = {}, meta = {}) {
  const S = { summary: true, notes: true, decisions: true, tasks: true, transcript: false, ...sections };
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const BLUE = [105, 148, 198];
  const BLUE_D = [61, 106, 158];
  const INK = [33, 46, 64];
  const BODY = [58, 68, 86];
  const MUTED = [140, 148, 160];
  const GREEN = [90, 125, 82];
  const LINE = [232, 230, 220];
  const PAGE_W = 210, PAGE_H = 297;
  const MARGIN = 20;
  const CONTENT_W = PAGE_W - MARGIN * 2;
  const BOTTOM = 282;
  let y = 0;

  const ensure = (need = 8) => { if (y > BOTTOM - need) { doc.addPage(); y = 24; } };

  const addText = (text, x, yPos, opts = {}) => {
    const { size = 9.5, color = BODY, style = 'normal', font = 'helvetica', maxWidth, lh = 1.28 } = opts;
    doc.setFont(font, style); doc.setFontSize(size); doc.setTextColor(...color);
    const lines = doc.splitTextToSize(String(text ?? ''), maxWidth || CONTENT_W);
    doc.text(lines, x, yPos);
    return yPos + lines.length * size * 0.3528 * lh;
  };

  const sectionHeader = (title) => {
    ensure(16);
    y += 3;
    doc.setDrawColor(...LINE); doc.setLineWidth(0.3);
    doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    y += 6;
    doc.setFillColor(...BLUE);
    doc.roundedRect(MARGIN, y - 3.2, 3, 3, 0.6, 0.6, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...BLUE_D);
    doc.text(title.toUpperCase(), MARGIN + 5, y);
    doc.setCharSpace(0);
    y += 6.5;
  };

  const bullet = (txt, dotColor) => {
    ensure(8);
    doc.setFillColor(...dotColor);
    doc.circle(MARGIN + 1.2, y - 1, 0.9, 'F');
    y = addText(txt, MARGIN + 5, y, { size: 9.5, color: BODY, maxWidth: CONTENT_W - 5 });
    y += 2.6;
  };

  // ── Header band ──
  doc.setFillColor(...INK);
  doc.rect(0, 0, PAGE_W, 20, 'F');
  try {
    const logo = await loadImageDataUrl(PDF_LOGO);
    const logoH = 7;
    doc.addImage(logo.dataUrl, 'PNG', MARGIN, 6.5, logo.w * (logoH / logo.h), logoH);
  } catch {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(244, 244, 236);
    doc.text('milk', MARGIN, 12.5);
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...BLUE);
  doc.setCharSpace(1.2);
  doc.text('ACTION CARD', PAGE_W - MARGIN, 12, { align: 'right' });
  doc.setCharSpace(0);

  // ── Title block ──
  y = 34;
  y = addText(card.title || card.contact_name || 'Action Card', MARGIN, y, { size: 22, color: INK, style: 'bold', font: 'times', lh: 1.12 });
  y += 4;

  const metaBits = [];
  if (card.created_at) metaBits.push(new Date(card.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
  if (meta.preparedBy) metaBits.push(`Prepared by ${meta.preparedBy}`);
  if (card.contact_name && card.contact_name !== (card.title || '')) metaBits.push(`Contact: ${card.contact_name}`);
  if (metaBits.length) { y = addText(metaBits.join('   ·   '), MARGIN, y, { size: 9, color: MUTED }); y += 1; }

  // Tags as pills
  if (card.tags?.length) {
    y += 3.5;
    let tx = MARGIN;
    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    card.tags.slice(0, 8).forEach((tag) => {
      const w = doc.getTextWidth(tag) + 6;
      if (tx + w > MARGIN + CONTENT_W) return;
      doc.setFillColor(238, 243, 250);
      doc.roundedRect(tx, y - 3.6, w, 5.4, 2.7, 2.7, 'F');
      doc.setTextColor(...BLUE_D);
      doc.text(tag, tx + 3, y);
      tx += w + 3;
    });
    y += 3.5;
  }
  y += 6;

  // ── Summary ──
  if (S.summary && card.summary_bullets?.length) {
    sectionHeader('Summary');
    card.summary_bullets.forEach((b) => bullet(b, BLUE));
    y += 3;
  }

  // ── Notes (rich) ──
  const noteBlocks = S.notes ? parseNotesHtml(card.summary_note) : [];
  if (noteBlocks.length) {
    sectionHeader('Notes');
    noteBlocks.forEach((blk) => {
      if (blk.type === 'h') {
        ensure(10); y += 1.5;
        y = addText(blk.text, MARGIN, y, { size: 10.5, color: INK, style: 'bold' });
        y += 2;
      } else if (blk.type === 'li') {
        ensure(8);
        doc.setFillColor(...BLUE); doc.circle(MARGIN + 3, y - 1, 0.8, 'F');
        y = addText(blk.text, MARGIN + 7, y, { size: 9.5, color: BODY, maxWidth: CONTENT_W - 7 });
        y += 2.2;
      } else {
        ensure(8);
        y = addText(blk.text, MARGIN, y, { size: 9.5, color: BODY });
        y += 2.6;
      }
    });
    y += 3;
  }

  // ── Decisions ──
  if (S.decisions && card.decisions?.length) {
    sectionHeader('Decisions');
    card.decisions.forEach((d) => bullet(d, GREEN));
    y += 3;
  }

  // ── Tasks ──
  if (S.tasks && tasks?.length) {
    sectionHeader('Tasks');
    const headers = ['Task', 'Owner', 'Due', 'Priority', 'Status'];
    const colW = [70, 30, 26, 20, 20];
    const rowH = 7.5;
    const drawHead = () => {
      doc.setFillColor(...INK);
      doc.roundedRect(MARGIN, y - 4.6, CONTENT_W, rowH, 1.2, 1.2, 'F');
      let x = MARGIN + 2;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.8); doc.setTextColor(244, 244, 236);
      headers.forEach((h, i) => { doc.text(h.toUpperCase(), x, y); x += colW[i]; });
      y += rowH;
    };
    drawHead();
    tasks.forEach((task, idx) => {
      if (y > BOTTOM - rowH) { doc.addPage(); y = 24; drawHead(); }
      if (idx % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(MARGIN, y - 4.6, CONTENT_W, rowH, 'F'); }
      const done = task.status === 'done';
      const txtColor = done ? [165, 170, 180] : BODY;
      const pri = task.priority || '—';
      const priColor = pri === 'high' ? [180, 51, 46] : pri === 'low' ? MUTED : [138, 109, 59];
      let x = MARGIN + 2;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.2);
      // Task title (clamp to 2 lines)
      doc.setTextColor(...txtColor);
      const tLines = doc.splitTextToSize(String(task.title || ''), colW[0] - 3).slice(0, 2);
      doc.text(tLines, x, y); x += colW[0];
      doc.setTextColor(...txtColor);
      doc.text(doc.splitTextToSize(String(task.owner || '—'), colW[1] - 3).slice(0, 1), x, y); x += colW[1];
      doc.text(String(task.due_date || '—').slice(0, 16), x, y); x += colW[2];
      doc.setTextColor(...priColor); doc.setFont('helvetica', 'bold');
      doc.text(String(pri).charAt(0).toUpperCase() + String(pri).slice(1), x, y); x += colW[3];
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(done ? GREEN : MUTED));
      doc.text(done ? 'Done' : 'Open', x, y);
      y += Math.max(rowH, tLines.length * 3.2 + 3.5);
    });
    y += 4;
  }

  // ── Transcript ──
  if (S.transcript && card.transcript) {
    sectionHeader('Transcript');
    String(card.transcript).split('\n').map((l) => l.trim()).filter(Boolean).forEach((line) => {
      ensure(6);
      y = addText(line, MARGIN, y, { size: 8.5, color: BODY, lh: 1.24 });
      y += 1.4;
    });
    y += 3;
  }

  // ── Footer on every page ──
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...LINE); doc.setLineWidth(0.3);
    doc.line(MARGIN, PAGE_H - 12, PAGE_W - MARGIN, PAGE_H - 12);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...BLUE_D);
    doc.text('Milk', MARGIN, PAGE_H - 7.5);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
    doc.text('Conversations, captured.', MARGIN + 8, PAGE_H - 7.5);
    doc.text(`${i} / ${totalPages}`, PAGE_W - MARGIN, PAGE_H - 7.5, { align: 'right' });
  }

  const safeName = (card.title || card.contact_name || 'action-card').replace(/[^a-z0-9]/gi, '-').toLowerCase().slice(0, 60);
  doc.save(`${safeName}-action-card.pdf`);
}
